import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, Upload, Clock, ArrowLeft, Send, AlertCircle, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientForm = () => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    phone_country_code: '+33',
    client_address: '',
    description: '',
    photo_base64_list: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [priceEstimation, setPriceEstimation] = useState(null);

  // Country codes for phone (France, DOM-TOM, Francophonie, Anglophones)
  const countryCodes = [
    // France métropolitaine
    { code: '+33', country: 'France', flag: '🇫🇷' },
    // DOM-TOM
    { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
    { code: '+594', country: 'Guyane', flag: '🇬🇫' },
    { code: '+596', country: 'Martinique', flag: '🇲🇶' },
    { code: '+262', country: 'Réunion', flag: '🇷🇪' },
    { code: '+269', country: 'Mayotte', flag: '🇾🇹' },
    { code: '+508', country: 'Saint-Pierre-et-Miquelon', flag: '🇵🇲' },
    { code: '+681', country: 'Wallis-et-Futuna', flag: '🇼🇫' },
    { code: '+689', country: 'Polynésie française', flag: '🇵🇫' },
    { code: '+687', country: 'Nouvelle-Calédonie', flag: '🇳🇨' },
    // Pays francophones
    { code: '+32', country: 'Belgique', flag: '🇧🇪' },
    { code: '+41', country: 'Suisse', flag: '🇨🇭' },
    { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' },
    // Pays anglophones
    { code: '+44', country: 'Royaume-Uni', flag: '🇬🇧' },
    { code: '+1', country: 'États-Unis', flag: '🇺🇸' },
    { code: '+61', country: 'Australie', flag: '🇦🇺' },
    { code: '+64', country: 'Nouvelle-Zélande', flag: '🇳🇿' },
    { code: '+353', country: 'Irlande', flag: '🇮🇪' },
    { code: '+27', country: 'Afrique du Sud', flag: '🇿🇦' },
    // Autres pays européens
    { code: '+49', country: 'Allemagne', flag: '🇩🇪' },
    { code: '+34', country: 'Espagne', flag: '🇪🇸' },
    { code: '+39', country: 'Italie', flag: '🇮🇹' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+31', country: 'Pays-Bas', flag: '🇳🇱' },
  ];

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\s/g, '');
    return /^\d{9,10}$/.test(cleaned);
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5').trim();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          photo_base64_list: [...prev.photo_base64_list, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, client_phone: formatted });
    if (validationErrors.phone && validatePhone(formatted)) {
      setValidationErrors(prev => ({ ...prev, phone: null }));
    }
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, client_email: email });
    if (validationErrors.email && validateEmail(email)) {
      setValidationErrors(prev => ({ ...prev, email: null }));
    }
  };

  const handleShowRecap = (e) => {
    e.preventDefault();
    
    // Validate fields
    const errors = {};
    if (!validateEmail(formData.client_email)) {
      errors.email = 'Veuillez entrer une adresse email valide';
    }
    if (!validatePhone(formData.client_phone)) {
      errors.phone = 'Veuillez entrer un numéro de téléphone valide (10 chiffres)';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }
    
    setValidationErrors({});
    setShowRecap(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fullPhone = `${formData.phone_country_code} ${formData.client_phone}`;
      const response = await axios.post(`${API}/public/quote-request`, {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: fullPhone,
        client_address: formData.client_address,
        description: formData.description,
        photo_base64_list: formData.photo_base64_list
      });
      
      // Store price estimation if available
      if (response.data.estimation) {
        setPriceEstimation(response.data.estimation);
      }
      
      toast.success('Demande envoyée !');
      setSubmitted(true);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  // Page de confirmation finale
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-50 to-slate-100">
        <Card className="max-w-md w-full p-6 text-center bg-white border-0 shadow-xl rounded-2xl">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Merci {formData.client_name.split(' ')[0]} !</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            Votre demande de devis a bien été enregistrée. Notre équipe l'analyse et vous enverra un devis détaillé.
          </p>
          
          {/* Price estimation */}
          {priceEstimation && priceEstimation.has_estimation && (
            <div className="bg-amber-50 rounded-xl p-4 mb-6 border border-amber-200">
              <div className="flex items-center justify-center gap-2 text-amber-700 mb-2">
                <Calculator className="w-5 h-5" />
                <span className="font-semibold">Estimation indicative</span>
              </div>
              <p className="text-2xl font-bold text-amber-900 mb-2">
                {priceEstimation.price_min.toLocaleString('fr-FR')} € - {priceEstimation.price_max.toLocaleString('fr-FR')} € HT
              </p>
              <div className="flex items-start gap-2 p-2 bg-amber-100/50 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 text-left">
                  <strong>Estimation non contractuelle.</strong> Le devis final sera établi après analyse détaillée de votre projet.
                </p>
              </div>
            </div>
          )}
          
          <div className="bg-blue-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-blue-700 mb-2">
              <Clock className="w-5 h-5" />
              <span className="font-semibold">Délai moyen : moins de 24h</span>
            </div>
            <p className="text-sm text-blue-600">
              Vous recevrez votre devis par email à<br/>
              <strong>{formData.client_email}</strong>
            </p>
          </div>

          <p className="text-xs text-slate-400">
            Un email de confirmation vous a été envoyé
          </p>
        </Card>
      </div>
    );
  }

  // Page de récapitulatif avant envoi
  if (showRecap) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-6 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="bg-primary/5 p-4 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900 text-center">Récapitulatif de votre demande</h2>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Client info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3 text-sm uppercase tracking-wide">Vos coordonnées</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-slate-500">Nom :</span> <span className="font-medium">{formData.client_name}</span></p>
                  <p><span className="text-slate-500">Email :</span> <span className="font-medium">{formData.client_email}</span></p>
                  <p><span className="text-slate-500">Tél :</span> <span className="font-medium">{formData.phone_country_code} {formData.client_phone}</span></p>
                  <p><span className="text-slate-500">Adresse :</span> <span className="font-medium">{formData.client_address}</span></p>
                </div>
              </div>

              {/* Project */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-slate-700 mb-3 text-sm uppercase tracking-wide">Votre projet</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{formData.description}</p>
                {formData.photo_base64_list.length > 0 && (
                  <p className="text-sm text-emerald-600 mt-2">📷 {formData.photo_base64_list.length} photo(s) jointe(s)</p>
                )}
              </div>

              {/* Delivery info */}
              <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
                <Clock className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-blue-800">Devis sous 24h en moyenne</p>
                  <p className="text-sm text-blue-600">Vous recevrez votre devis détaillé par email</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowRecap(false)}
                  className="flex-1 h-12"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 h-12 bg-primary text-white"
                >
                  {submitting ? 'Envoi...' : 'Confirmer'}
                  {!submitting && <Send className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Formulaire principal
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-6 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Demande de devis gratuit</h1>
          <p className="text-slate-500 text-sm">Réponse sous 24h • Sans engagement</p>
        </div>

        {/* Form Card */}
        <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
          <form onSubmit={handleShowRecap} className="p-5 space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="client_name" className="text-sm font-medium text-slate-700">
                Nom complet *
              </Label>
              <Input
                id="client_name"
                data-testid="client-name-input"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="mt-1.5 h-12 rounded-xl border-slate-200 focus:border-primary"
                placeholder="Jean Dupont"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="client_email" className="text-sm font-medium text-slate-700">
                Email *
              </Label>
              <Input
                id="client_email"
                data-testid="client-email-input"
                type="email"
                required
                value={formData.client_email}
                onChange={handleEmailChange}
                className={`mt-1.5 h-12 rounded-xl ${validationErrors.email ? 'border-red-500 focus:border-red-500' : 'border-slate-200'}`}
                placeholder="email@exemple.com"
              />
              {validationErrors.email && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.email}
                </p>
              )}
            </div>

            {/* Phone with country code */}
            <div>
              <Label htmlFor="client_phone" className="text-sm font-medium text-slate-700">
                Téléphone *
              </Label>
              <div className="flex gap-2 mt-1.5">
                <select
                  value={formData.phone_country_code}
                  onChange={(e) => setFormData({ ...formData, phone_country_code: e.target.value })}
                  className="h-12 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="phone-country-select"
                >
                  {countryCodes.map(({ code, country, flag }) => (
                    <option key={code} value={code}>
                      {flag} {code}
                    </option>
                  ))}
                </select>
                <Input
                  id="client_phone"
                  data-testid="client-phone-input"
                  type="tel"
                  required
                  value={formData.client_phone}
                  onChange={handlePhoneChange}
                  className={`flex-1 h-12 rounded-xl ${validationErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-slate-200'}`}
                  placeholder="06 12 34 56 78"
                />
              </div>
              {validationErrors.phone && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.phone}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="client_address" className="text-sm font-medium text-slate-700">
                Adresse du chantier *
              </Label>
              <Input
                id="client_address"
                data-testid="client-address-input"
                required
                value={formData.client_address}
                onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                className="mt-1.5 h-12 rounded-xl border-slate-200"
                placeholder="123 Rue Example, 75001 Paris"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                Description des travaux *
              </Label>
              <Textarea
                id="description"
                data-testid="description-textarea"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1.5 min-h-28 rounded-xl border-slate-200 resize-none"
                placeholder="Décrivez votre projet en détail..."
              />
            </div>

            {/* Photos */}
            <div>
              <Label htmlFor="photos" className="text-sm font-medium text-slate-700">
                Photos <span className="text-slate-400 font-normal">(optionnel)</span>
              </Label>
              <div className="mt-1.5 border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-primary/50 transition-colors">
                <input
                  id="photos"
                  data-testid="photos-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="photos" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Cliquez pour ajouter des photos</p>
                  <p className="text-xs text-slate-400">JPG, PNG jusqu'à 10 Mo</p>
                </label>
              </div>
              {formData.photo_base64_list.length > 0 && (
                <p className="text-sm text-emerald-600 mt-2 text-center">
                  ✓ {formData.photo_base64_list.length} photo(s) sélectionnée(s)
                </p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full h-12 bg-primary text-white hover:bg-primary/90 font-medium rounded-xl text-base mt-4"
              data-testid="submit-form-button"
            >
              Voir le récapitulatif
            </Button>

            <p className="text-xs text-slate-400 text-center">
              🔒 Vos données sont confidentielles
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ClientForm;
